/**
 * File: client/src/components/PropertyDashboard.js
 * ===================================================
 * Route: /
 *
 * Role:
 * - Displays all properties in the portfolio
 * - Primary navigation entry point
 * - Canonical place where property URLs are generated
 *
 * Routing rules:
 * - Prefer human-readable address slugs
 * - Fallback to system-safe property ID when address is missing
 *
 * IMPORTANT:
 * - Optional fields (like address) can NEVER be primary keys
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStreetViewUrl } from "../utils/geocoding.js";
import {
  PlusCircle,
  CalendarDays,
  ShieldCheck,
  PiggyBank,
} from "lucide-react";

/**
 * slugify
 * ---------------------------------------------------
 * Converts a string into a URL-safe slug
 * Must match logic used in PropertyDetails.js
 */
const slugify = (str = "") =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function PropertyDashboard() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch all properties on mount
   */
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/api/properties");
        const data = await res.json();
        setProperties(data);
      } catch (err) {
        console.error("Failed to load properties", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  /**
   * Normalize image URLs - use Street View as fallback
   */
  const resolveImageUrl = (prop) => {
    if (prop.imageUrl) {
      if (prop.imageUrl.startsWith("http")) return prop.imageUrl;
      return prop.imageUrl;
    }
    // Fallback to Street View if we have address or coordinates
    if (prop.address) {
      return getStreetViewUrl(prop.address, { size: "600x400" });
    }
    if (prop.lat && prop.lng) {
      return getStreetViewUrl(`${prop.lat},${prop.lng}`, { size: "600x400" });
    }
    return null;
  };

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto bg-hn-white text-hn-dark space-y-10">

      {/* =================================================
          HEADER
         ================================================= */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-lemonmilk tracking-tight">
          Your Properties
        </h1>

        <Link
          to="/add-property"
          className="flex items-center gap-2 px-4 py-2 bg-hn-red text-white rounded-lg shadow-sm hover:bg-red-700 transition"
        >
          <PlusCircle className="w-5 h-5" />
          Add Property
        </Link>
      </div>

      {/* =================================================
          LOADING STATE
         ================================================= */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl bg-gray-200 h-64" />
          ))}
        </div>
      )}

      {/* =================================================
          PROPERTIES GRID
         ================================================= */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((p) => {
            const imageSrc = resolveImageUrl(p);

            // 🔑 Robust routing identity
            const slug = p.address
              ? slugify(p.address)
              : `property-${p.id}`;

            const valuation = p.valuation?.estimate ?? null;

            return (
              <Link
                key={p.id}
                to={`/property/${slug}`}
                className="bg-white rounded-xl shadow-sm border border-hn-dark/5 hover:shadow-lg transition overflow-hidden group"
              >
                {/* IMAGE */}
                <div>
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={p.name || "Property"}
                      className="w-full aspect-video object-cover"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  ) : (
                    <div className="w-full aspect-video bg-gray-200 flex items-center justify-center text-gray-500">
                      No Image
                    </div>
                  )}
                </div>

                {/* CONTENT */}
                <div className="p-5 space-y-3">
                  <h2 className="text-lg font-lemonmilk">
                    {p.name || "Property"}
                  </h2>

                  {p.address ? (
                    <p className="text-sm text-gray-600">{p.address}</p>
                  ) : (
                    <p className="text-sm italic text-gray-400">
                      Address not set
                    </p>
                  )}

                  {/* CORE STATS */}
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="font-semibold text-hn-red">
                        Valuation:
                      </span>{" "}
                      {valuation
                        ? `$${valuation.toLocaleString()}`
                        : "—"}
                    </p>

                    {p.type && (
                      <p>
                        <span className="font-semibold text-hn-red">
                          Type:
                        </span>{" "}
                        {p.type}
                      </p>
                    )}

                    {p.sqft && (
                      <p>
                        <span className="font-semibold text-hn-red">
                          Size:
                        </span>{" "}
                        {p.sqft.toLocaleString()} sq ft
                      </p>
                    )}
                  </div>

                  {/* INSIGHTS BAR (placeholders) */}
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <div className="flex items-center gap-1 bg-gray-100 py-2 px-2 rounded-lg">
                      <CalendarDays className="w-4 h-4 text-hn-red" />
                      <span className="truncate">
                        {p.nextMaintenance ?? "No tasks"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 bg-gray-100 py-2 px-2 rounded-lg">
                      <ShieldCheck className="w-4 h-4 text-hn-red" />
                      <span className="truncate">
                        {p.insuranceRenewal ?? "Insurance"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 bg-gray-100 py-2 px-2 rounded-lg">
                      <PiggyBank className="w-4 h-4 text-hn-red" />
                      <span className="truncate">
                        {p.mortgageRenewal ?? "Mortgage"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* CTA BAR */}
                <div className="p-4 bg-hn-red/5 flex justify-between opacity-0 group-hover:opacity-100 transition text-xs font-medium">
                  <span className="text-hn-red">View Details →</span>
                  <span className="text-hn-red">Run Analysis</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
